/**
 * PF Revamp — v2 instruction-options query.
 *
 * ONE POST replaces the legacy Path-A shim (mint a LEGACY session via
 * `getCategoryConfig` → POST legacy `/order/configuration/instruction`):
 *   `POST /workspace/{ws}/sessions/{sessionId}/instructions`
 * → `PurchaseStepInstructionV2DTO` = `visible` (exact skip signal) +
 *   `timezone` (shoot-location IANA tz) + `billingAddress` (
 *   BE-confirmed: structurally always null for workspace clients — wire key
 *   only, never consumed; see the DTO comment) + per-flavor options with
 *   `isDefault` and a nullable gross `price`.
 *
 * POST-as-READ: the endpoint is a pure projection of (session × productCodes ×
 * coordinates) with no side effects, so it is modelled as a `useQuery` with the
 * body in the cache key, NOT a
 * mutation. Consequences, all desired:
 *   - a cart edit that changes the resolved variant `productCode` set refetches;
 *     a pure quantity change does not (quantity is not in the body),
 *   - `placeholderData: (prev) => prev` keeps the previous options on screen
 *     during that refetch, so the meeting-mode radio never flashes back to its
 *     `loading → []` state,
 *   - every consumer (Instructions page, Billing page, `useResolvedStepFlow`)
 *     shares ONE cache entry because `ws`/`sessionId` are resolved INTERNALLY,
 *     exactly like `useCatalogue`.
 *
 * BE notes (ground truth 2026-07-29):
 *   - the request is strictly Product2-bound: a product whose Product2 has no
 *     category ⇒ 422.
 *   - the admin mimetype excludes direct-payment options (client sees
 *     card/SEPA, admin does not) — handled by the Billing availability ladder,
 *     `steps/billing/billing.helpers.ts`.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type {
  CartItem,
  InstructionCoordinatesV2,
  InstructionOptionV2DTO,
  OrderInstructionsRequestV2DTO,
  PurchaseStepInstructionV2DTO,
  ResolvedCatalogue,
  ResolvedInstructionOption
} from 'models/purchaseFlowRevamp'
import type { ProductKind } from 'constants/product'
import { InstructionType } from 'constants/purchaseFlow'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'

import { usePfCatalogue } from './purchaseFlowRevamp.query'
import { resolveCartProductCodes } from './purchaseFlowRevampProductCodes'
import { isRoutable } from 'components/pages/PurchaseFlowRevamp/entry/usePropertyCompleteness'
import { useResolvedPropertyCoordinates } from 'components/pages/PurchaseFlowRevamp/entry/useResolvedPropertyCoordinates'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

enum Endpoints {
  /** POST — v2 session-bound instruction options (organization / billing / delivery). */
  SESSION_INSTRUCTIONS = '/workspace/{workspaceId}/sessions/{sessionId}/instructions',
}

/** Status vocabulary shared with `useInstructionCatalogue` + `resolveMeetingModes`. */
export type InstructionOptionsStatus = 'idle' | 'loading' | 'ready' | 'error'

/* -------------------------------------------------------------------------- *
 *  Pure helpers
 * -------------------------------------------------------------------------- */

/**
 * Build the request body from the cart × resolved catalogue × property coords.
 * Returns `null` when no product code resolves — the wire requires
 * `minItems: 1`, so the hook stays disabled (`status: 'idle'`) instead of
 * posting an empty array.
 *
 * Codes come from the shared `resolveCartProductCodes` — de-duplicated and
 * SORTED, so the React Query cache key is stable across cart reordering and
 * quantity edits (only the *set* of resolved codes matters to the BE), and
 * identical to the set the available-creatives POST describes.
 *
 * COORDINATES ARE OMITTED WHEN THERE ARE NONE. This used to send
 * `{ lat: 0, lng: 0 }` to dodge the BE's "No coordinates given" 422 for
 * non-editing categories. That dodge bought a 200 whose `timezone` was always
 * null: the Time Zone API answers ZERO_RESULTS for the null island, so the
 * lookup could only fail — silently disabling the lead-time floor and the
 * timezone banner, and indistinguishable on the wire from the editing cart
 * where a null timezone is correct. The backend now refuses (0,0) explicitly
 * and meters it as a caller bug.
 *
 * So the 422 comes back for a shooting cart with no routable coordinates, and
 * that is the honest answer: such a property is not orderable anyway — the
 * entry drawer's own completeness gate requires routable coordinates before it
 * will let one through (`usePropertyCompleteness`). An all-editing cart is
 * unaffected; the backend substitutes its own placeholder and never looks the
 * timezone up.
 *
 * R-2 CLOSED (ledger #13): BE resolves every code and derives option
 * availability from the union of their kinds (e.g. FAST_DELIVERY only offered
 * with a GROUND_PHOTO/DRONE_PHOTO kind present; the zero-price
 * ORGANIZATION_THIRD_PARTY variant only applies with MEASUREMENT_ON_SITE
 * present) — sending variant codes alone silently dropped options. Every
 * selected addon/extra code is now included too, resolved via the SAME
 * `optionValues` / `extraServiceIds` → `productCode` selection rules
 * `serializePlacement.buildAddons` uses for the place-order body (BOOLEAN
 * truthy / NUMBER > 0 / extras-by-id; ENUM skipped — it shapes the variant
 * choice, not a standalone product). Variant codes are resolved first, addon
 * codes second, before the final dedupe + sort.
 */
export const buildInstructionOptionsBody = (
  items: ReadonlyArray<CartItem>,
  catalogue: ResolvedCatalogue | undefined,
  coordinates?: InstructionCoordinatesV2
): OrderInstructionsRequestV2DTO | null => {
  const codes = resolveCartProductCodes(items, catalogue)

  if (codes.length === 0) return null

  return {
    productCodes: codes,
    // Spread rather than `coordinates: undefined`, so the key is absent from the
    // JSON rather than present and null — the backend distinguishes the two.
    ...(isRoutable(coordinates) ? { coordinates } : {}),
  }
}

/** Wire option → UI projection. Flattens the nullable `MonetaryValueDTO`, and
 *  normalises the nullable `impliedFeeProductCode` to `undefined` (V-16 — its
 *  presence is what makes `price` a real charge to mirror into the cart total). */
export const toResolvedInstructionOption = (wire: InstructionOptionV2DTO): ResolvedInstructionOption => ({
  productCode: wire.productCode,
  kind: wire.kind as ProductKind,
  isDefault: wire.isDefault,
  amount: wire.price?.amount,
  currency: wire.price?.currency,
  impliedFeeProductCode: wire.impliedFeeProductCode ?? undefined,
  fields: wire.fields ?? [],
})

/** Flavors that are neither meeting options nor extras: BILLING and CT_BOOKING
 *  are their own buckets, BILLING_PRIMARY / VAT_NUMBER are Billing-form concerns
 *  the v2 Billing UI models natively and must never leak into the extras
 *  toggles. */
const NON_EXTRA_FLAVORS: ReadonlySet<string> = new Set<string>([
  InstructionType.ORGANIZATION,
  InstructionType.BILLING,
  InstructionType.BILLING_PRIMARY,
  InstructionType.VAT_NUMBER,
  InstructionType.CT_BOOKING,
])

/** Buckets produced by {@link bucketInstructionOptions}. */
export interface BucketedInstructionOptions {
  /** ProductKind-keyed ORGANIZATION options → `resolveMeetingModes`. */
  organization: Partial<Record<ProductKind, ResolvedInstructionOption>>
  /** ProductKind-keyed order-level extras (DELIVERY + any unknown flavor). */
  extras: Partial<Record<ProductKind, ResolvedInstructionOption>>
  /** BILLING options in BE order → the payment-method availability ladder. */
  billingOptions: ResolvedInstructionOption[]
  /** CT_BOOKING options in BE order → the "Book a favourite creative" section's
   *  visibility signal (W-11). An EMPTY array is meaningful: the BE answered and
   *  advertised no CT booking for this cart, so the section must not render. */
  ctBookingOptions: ResolvedInstructionOption[]
  /** First non-null option currency across all flavors (BE is single-currency
   *  per org); `undefined` when every option is unpriced. */
  currency?: string
}

/**
 * Split the response into the three buckets the UI consumes.
 *
 * Rules (parity with the legacy `PurchaseFlowConfig.context.ts:204-256`
 * fall-through, adjusted for the v2 flavors):
 *   - ORGANIZATION → `organization`, keyed by kind. A wire `kind: 'NONE'` is
 *     IGNORED: "no meeting" is synthetic FE-side and always offered, so writing
 *     it into the map would key a bogus mode.
 *   - BILLING → `billingOptions` only (never extras).
 *   - CT_BOOKING → `ctBookingOptions` only (W-11). The favourite-creative picker
 *     is its own section with its own wire slot (`selectedFavouriteCreativeId`),
 *     so its FAVOURITE_CREATIVE option is NOT an order-level extra — leaving it
 *     in `extras` rendered the same offer twice (once as the picker, once as a
 *     +0.00 toggle that serializes to nothing).
 *   - BILLING_PRIMARY / VAT_NUMBER → dropped (native Billing form owns those).
 *   - DELIVERY **and any unrecognised flavor** → `extras`, so a new BE flavor
 *     surfaces in the UI instead of vanishing.
 */
export const bucketInstructionOptions = (
  dto: PurchaseStepInstructionV2DTO | undefined
): BucketedInstructionOptions => {
  const organization: Partial<Record<ProductKind, ResolvedInstructionOption>> = {}
  const extras: Partial<Record<ProductKind, ResolvedInstructionOption>> = {}
  const billingOptions: ResolvedInstructionOption[] = []
  const ctBookingOptions: ResolvedInstructionOption[] = []
  let currency: string | undefined

  for (const [flavor, group] of Object.entries(dto?.instructionTypes ?? {})) {
    if (!group) continue
    // Trust the map key over `group.key` — the key is what the BE documents as
    // the flavor and the only thing guaranteed present on an object entry.
    const key = flavor || group.key

    for (const wire of group.instructionOptions ?? []) {
      const option = toResolvedInstructionOption(wire)
      if (currency === undefined && option.currency) currency = option.currency

      if (key === InstructionType.ORGANIZATION) {
        if (option.kind === 'NONE') continue
        organization[option.kind] = option
        continue
      }

      if (key === InstructionType.BILLING) {
        billingOptions.push(option)
        continue
      }

      if (key === InstructionType.CT_BOOKING) {
        ctBookingOptions.push(option)
        continue
      }

      if (NON_EXTRA_FLAVORS.has(key)) continue

      extras[option.kind] = option
    }
  }

  return { organization, extras, billingOptions, ctBookingOptions, currency }
}

/* -------------------------------------------------------------------------- *
 *  Mock
 * -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- *
 *  Hook
 * -------------------------------------------------------------------------- */

/** Result of {@link useInstructionOptions}. */
export interface UseInstructionOptionsResult {
  data?: PurchaseStepInstructionV2DTO
  status: InstructionOptionsStatus
  /** `query.error?.message`, kept a string for the public hook shape. */
  error?: string
  /** `true` when the inputs are incomplete (no session / workspace / cart) so
   *  the query never fired — callers render today's pre-catalogue UI. */
  isSkipped: boolean
  /** Re-runs the request, so a failed step can offer a retry rather
   *  than leaving the user with a silently guessed form. */
  refetch: () => void
  /** `true` while `data` is still the previous cart's answer and the current cart's is on its way. */
  isRefreshing: boolean
}

/**
 * React Query hook — the v2 instruction options for the current session + cart.
 *
 * `ws` / `sessionId` / body are resolved INTERNALLY (store + UserDataContext +
 * auth0), never passed by call sites, so all consumers share one cache entry.
 * Safe to mount on every step (`useResolvedStepFlow` does): it self-gates on
 * `ws && sessionId && productCodes.length > 0` and holds a 5-minute
 * `staleTime`, so the whole flow costs ONE POST plus one refetch per change to
 * the resolved variant-code set.
 *
 * @example
 * const { data, status } = useInstructionOptions()
 * const buckets = useMemo(() => bucketInstructionOptions(data), [data])
 */
export function useInstructionOptions(): UseInstructionOptionsResult {
  const api = useAPIV2<Endpoints>()

  const items = usePfStore((s) => s.items)
  const property = usePfStore((s) => s.property)
  const sessionId = usePfStore((s) => s.draft.sessionId)

  // Workspace: admin → picked target; client → its property workspace. Same
  // resolution as `usePfCatalogue`, so the two hooks agree on scope.
  const ws = usePfSessionWorkspaceId()

  // The cart keys on Salesforce variant ids; the wire wants product codes, so
  // the resolved catalogue is needed to translate.
  const catalogue = usePfCatalogue().data
  // The BE derives the shoot timezone from whatever we send. Prefer where the
  // property IS; fall back to the routable pin, because a timezone from the
  // nearest routable point is right in all but pathological cases and infinitely
  // better than the `(0,0)` last resort (open ocean → no timezone at all).
  //
  // Read through the recovery hook rather than off the store: a property
  // committed before its detail resolved carries none, and without them this
  // POST 422s for the life of the session — on a step where no property can be
  // fixed. See `useResolvedPropertyCoordinates`.
  const { coordinates, isRecovering } = useResolvedPropertyCoordinates(property, ws)

  const body = useMemo(
    () => buildInstructionOptionsBody(items, catalogue, coordinates),
    [items, catalogue, coordinates]
  )

  // Held while the recovery is in flight: firing now would send the omission
  // the recovery exists to prevent, and cache the 422 behind this key.
  const enabled = Boolean(ws && sessionId) && body !== null && !isRecovering

  const query = useQuery({
    queryKey: [
      EntityKeys.PF_REVAMP_INSTRUCTIONS,
      QueryType.GET_ONE,
      { ws, sessionId, body },
    ],
    queryFn: async (): Promise<PurchaseStepInstructionV2DTO> => {
      if (!ws || !sessionId || !body) throw new Error('instruction options called without a valid body')

      return api.post<PurchaseStepInstructionV2DTO>(
        Endpoints.SESSION_INSTRUCTIONS,
        { workspaceId: ws, sessionId },
        body,
        false
      )
    },
    enabled,
    // Options change only with the catalogue / product config, and the request
    // is re-keyed on the body, so a long stale window is safe.
    staleTime: 5 * 60_000,
    retry: 1,
    // Keep the previous options on screen while a cart edit refetches, so the
    // meeting-mode radio never drops back to its `loading → []` state.
    placeholderData: (prev) => prev,
  })

  const isSkipped = !enabled

  const status: InstructionOptionsStatus = isSkipped
    ? 'idle'
    : query.isError
      ? 'error'
      : query.data
        ? 'ready'
        : 'loading'

  return {
    data: query.data,
    status,
    error: query.error?.message,
    isSkipped,
    refetch: () => { void query.refetch() },
    isRefreshing: query.isPlaceholderData,
  }
}
